/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api, { settingsApi, authApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../hooks/useToast';
import { ArrowLeft, Plus, Edit2, Trash2, Search } from 'lucide-react';
import ToastContainer from '../hooks/ToastContainer';

interface Category {
  id: number;
  name: string;
  subcategory_count?: number;
}

interface Subcategory {
  id: number;
  category_id: number;
  name: string;
  category_name?: string;
}

interface Tag {
  id: number;
  name: string;
  story_count?: number;
}

interface Profile {
  name: string;
  email: string;
}

export default function Settings() {
  const navigate = useNavigate();
  const { updateProfile } = useAuth();
const { toast, toasts, dismiss } = useToast();
  
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(false);
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  console.log('subcategories',subcategories)
  const [tags, setTags] = useState<Tag[]>([]);
  const [popularTags, setPopularTags] = useState<Tag[]>([]);
  
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [subcategoryDialog, setSubcategoryDialog] = useState(false);
  const [tagDialog, setTagDialog] = useState(false);
  
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  
  const [categoryName, setCategoryName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [tagName, setTagName] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  
  const [profile, setProfile] = useState<Profile>({ name: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [appVersion, setAppVersion] = useState({
    rnAndroid:'',
    rnIos:''
  });


  useEffect(() => {
    if (activeTab === 'categories') {
      loadCategories();
      // loadSubcategories();
    } else if (activeTab === 'tags') {
      loadTags();
      loadPopularTags();
    } else if (activeTab === 'profile') {
      loadProfile();
    }
  }, [activeTab]);

  useEffect(() => {
    if (tagSearch) {
      const delayDebounceFn = setTimeout(() => {
        loadTags();
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    } else {
      loadTags();
    }
  }, [tagSearch]);

  const loadCategories = async () => {
    try {
      const res = await settingsApi.getCategories();
      setCategories(res.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSubcategories = async () => {
    try {
      const res = await settingsApi.getSubcategories();
      setSubcategories(res.data.subcategories);
    } catch (error) {
      console.error('Failed to load subcategories:', error);
    }
  };

  const loadTags = async () => {
    try {
      const res = await settingsApi.getTags();
      setTags(res.data.tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const loadPopularTags = async () => {
    try {
      const res = await settingsApi.getPopularTags();
      setPopularTags(res.data.tags);
    } catch (error) {
      console.error('Failed to load popular tags:', error);
    }
  };

  const loadProfile = async () => {
    try {
      const res = await authApi.getProfile();
      setProfile({
        name: res.data.user.name,
        email: res.data.user.email
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    
    setLoading(true);
    try {
      if (editingCategory) {
        await settingsApi.updateCategory(editingCategory.id, { name: categoryName });
        toast({ title: 'Success', description: 'Category updated successfully' });
      } else {
        await settingsApi.createCategory({ name: categoryName });
        toast({ title: 'Success', description: 'Category created successfully' });
      }
      
      setCategoryDialog(false);
      setCategoryName('');
      setEditingCategory(null);
      loadCategories();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save category',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) return;
    
    try {
      await settingsApi.deleteCategory(category.id);
      toast({ title: 'Success', description: 'Category deleted successfully' });
      loadCategories();
      loadSubcategories();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete category',
        variant: 'destructive'
      });
    }
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryName.trim() || !selectedCategoryId) return;
    
    setLoading(true);
    try {
      if (editingSubcategory) {
        await settingsApi.updateSubcategory(editingSubcategory.id, {
          name: subcategoryName,
          category_id: parseInt(selectedCategoryId)
        });
        toast({ title: 'Success', description: 'Subcategory updated successfully' });
      } else {
        await settingsApi.createSubcategory({
          name: subcategoryName,
          category_id: selectedCategoryId
        });
        toast({ title: 'Success', description: 'Subcategory created successfully' });
      }
      
      setSubcategoryDialog(false);
      setSubcategoryName('');
      setSelectedCategoryId('');
      setEditingSubcategory(null);
      loadSubcategories();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save subcategory',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleDeleteSubcategory = async (subcategory: Subcategory) => {
  //   if (!window.confirm(`Are you sure you want to delete "${subcategory.name}"?`)) return;
    
  //   try {
  //     await settingsApi.deleteSubcategory(subcategory.id);
  //     toast({ title: 'Success', description: 'Subcategory deleted successfully' });
  //     loadSubcategories();
  //   } catch (error: any) {
  //     toast({
  //       title: 'Error',
  //       description: error.response?.data?.error || 'Failed to delete subcategory',
  //       variant: 'destructive'
  //     });
  //   }
  // };

  const handleSaveTag = async () => {
    if (!tagName.trim()) return;
    
    setLoading(true);
    try {
      if (editingTag) {
        await settingsApi.updateTag(editingTag.id, { name: tagName });
        toast({ title: 'Success', description: 'Tag updated successfully' });
      } else {
        await settingsApi.createTag({ name: tagName });
        toast({ title: 'Success', description: 'Tag created successfully' });
      }
      
      setTagDialog(false);
      setTagName('');
      setEditingTag(null);
      loadTags();
      loadPopularTags();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save tag',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (!window.confirm(`Are you sure you want to delete "${tag.name}"?`)) return;
    
    try {
      await settingsApi.deleteTag(tag.id);
      toast({ title: 'Success', description: 'Tag deleted successfully' });
      loadTags();
      loadPopularTags();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete tag',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateProfile = async () => {
    if (!profile.name.trim() || !profile.email.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name and email are required',
        variant: 'destructive'
      });
      return;
    }
    
    setProfileSaving(true);
    try {
      await updateProfile(profile);
      toast({ title: 'Success', description: 'Profile updated successfully' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setProfileSaving(false);
    }
  };

const versionRegex = /^\d+\.\d+\.\d+$/;

  const handleUpdateVersion  =async()=>{
// const res = axios.post(API_BASE_URL+ '/stories/admin/app-version')
console.log('handleUpdateVersion')
 if (!versionRegex.test(appVersion.rnAndroid)) {

  console.log('asdf')
  toast({
    title: "Invalid Version",
    description: "Android version must be like 1.0.0",
    variant: "destructive",
  });
  return;
}

   if (!versionRegex.test(appVersion.rnIos)) {
  toast({
    title: "Invalid Version",
    description: "Ios version must be like 1.0.0",
    variant: "destructive",
  });
  return;
}

const data ={
  appVersion:{
    ...appVersion
  }
}

const res = await api.post('/stories/admin/app-version',data)

console.log('res',res)
  }

  const fetchVersion =async()=>{
  const res = await  api.get('/stories/admin/app-version')

  console.log('res1111',res)
const value = res?.data?.data?.appVersion;
  setAppVersion({
    rnAndroid:value?.rnAndroid,
    rnIos:value?.rnIos
  })
  }

  useEffect(()=>{
fetchVersion()
  },[])
  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

  <ToastContainer toasts={toasts} dismiss={dismiss} />

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="tags">Tags</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
          </TabsList>

          <TabsContent value="categories" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Categories</CardTitle>
                    <CardDescription>Manage story categories</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingCategory(null);
                    setCategoryName('');
                    setCategoryDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories?.map(category => (
                    <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="font-medium">{category.name}</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingCategory(category);
                            setCategoryName(category.name);
                            setCategoryDialog(true);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteCategory(category)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {categories?.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No categories yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Subcategories</CardTitle>
                    <CardDescription>Manage subcategories under categories</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingSubcategory(null);
                    setSubcategoryName('');
                    setSelectedCategoryId('');
                    setSubcategoryDialog(true);
                  }} disabled={categories?.length === 0}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Subcategory
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {subcategories?.map(subcategory => {
                    const category = categories?.find(c => c?.id === subcategory?.category_id);
                    return (
                      <div key={subcategory.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <span className="font-medium">{subcategory?.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            ({category?.name || 'Unknown Category'})
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingSubcategory(subcategory);
                              setSubcategoryName(subcategory.name);
                              setSelectedCategoryId(subcategory.category_id.toString());
                              setSubcategoryDialog(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSubcategory(subcategory)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {subcategories?.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No subcategories yet</p>
                  )}
                </div>
              </CardContent>
            </Card> */}
          </TabsContent>

          <TabsContent value="tags" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Tags</CardTitle>
                    <CardDescription>Manage story tags</CardDescription>
                  </div>
                  <Button onClick={() => {
                    setEditingTag(null);
                    setTagName('');
                    setTagDialog(true);
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Tag
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      value={tagSearch}
                      onChange={(e) => setTagSearch(e.target.value)}
                      placeholder="Search tags..."
                      className="pl-10"
                    />
                  </div>
                </div>

                {popularTags?.length > 0 && !tagSearch && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">Popular Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {popularTags?.map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                        >
                          {tag.name}
                          <span className="text-xs opacity-70">({tag.story_count})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {tags?.map(tag => (
                    <div key={tag.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="font-medium">{tag.name}</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingTag(tag);
                            setTagName(tag.name);
                            setTagDialog(true);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteTag(tag)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {tags?.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      {tagSearch ? 'No tags found' : 'No tags yet'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>Update your profile information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleUpdateProfile} disabled={profileSaving}>
                    {profileSaving ? <Spinner size="sm" className="mr-2" /> : null}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

           <TabsContent value="versions">
             <Card>
              <CardHeader>
                <CardTitle>App Version</CardTitle>
                {/* <CardDescription>Update your profile information</CardDescription> */}
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Android Version</Label>
                  <Input
                    id="name"
                    placeholder='1.0.0'
                    value={appVersion?.rnAndroid}
                    onChange={(e) => setAppVersion({...appVersion,rnAndroid:e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Ios Version</Label>
                  <Input
                    id="name"
                      placeholder='1.0.0'
                    value={appVersion?.rnIos}
                    onChange={(e) => setAppVersion({...appVersion,rnIos:e.target.value})}
                    className="mt-1"
                  />
                </div>
                 <div className="flex justify-end">
                  <Button onClick={handleUpdateVersion} disabled={profileSaving}>
                    {profileSaving ? <Spinner size="sm" className="mr-2" /> : null}
                    Save Version
                  </Button>
                </div>
                </CardContent>
                </Card>
          </TabsContent>

        </Tabs>
      </main>

      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent onClose={() => setCategoryDialog(false)}>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update the category name' : 'Create a new category for stories'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} disabled={loading || !categoryName.trim()}>
              {loading ? <Spinner size="sm" className="mr-2" /> : null}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={subcategoryDialog} onOpenChange={setSubcategoryDialog}>
        <DialogContent onClose={() => setSubcategoryDialog(false)}>
          <DialogHeader>
            <DialogTitle>{editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</DialogTitle>
            <DialogDescription>
              {editingSubcategory ? 'Update the subcategory' : 'Create a new subcategory under a category'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="parent-category">Parent Category</Label>
              <select
                id="parent-category"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="mt-1 flex h-10 w-full appearance-none items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Select a category</option>
                {categories?.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="subcategory-name">Subcategory Name</Label>
              <Input
                id="subcategory-name"
                value={subcategoryName}
                onChange={(e) => setSubcategoryName(e.target.value)}
                placeholder="Enter subcategory name"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubcategoryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSubcategory} 
              disabled={loading || !subcategoryName.trim() || !selectedCategoryId}
            >
              {loading ? <Spinner size="sm" className="mr-2" /> : null}
              {editingSubcategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tagDialog} onOpenChange={setTagDialog}>
        <DialogContent onClose={() => setTagDialog(false)}>
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
            <DialogDescription>
              {editingTag ? 'Update the tag name' : 'Create a new tag for stories'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="tag-name">Tag Name</Label>
              <Input
                id="tag-name"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="Enter tag name"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTag} disabled={loading || !tagName.trim()}>
              {loading ? <Spinner size="sm" className="mr-2" /> : null}
              {editingTag ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}